import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CheckCircle, Leaf, X } from 'lucide-react';
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { COLORS } from '../constants';
import { SCOOTERS } from '../data/scooters';
import Button from '../components/Button';
import SectionHeading from '../components/SectionHeading';
import { startCheckout } from '../utils/checkout';



const ScootersPage = () => {
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [selectedScooter, setSelectedScooter] = useState(null);
  const [slideIndex, setSlideIndex] = useState(0);

  if (selectedScooter) {
    const images = selectedScooter.images || [selectedScooter.image];

    return (
    	<>
	<Helmet>
  	<title>Electric Golf Scooter Fleet | Lectric Leisure</title>
	<meta name="robots" content="index, follow" />
	<link rel="canonical" href="https://lectricleisure.golf/scooters" />
  	  <meta
  	    name="description"
  	    content="Browse the Lectric Leisure fleet – M12-1, M12-3, and ESGOLF. Engineered for stability and performance with immediate Australia-wide delivery and local support."/>	</Helmet>
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen py-27 px-16"
        style={{ backgroundColor: COLORS.lightSurface }}
      >
        <div className="container mx-auto max-w-6xl">
          <button
            onClick={() => {
              setSelectedScooter(null);
              setSlideIndex(0);
            }}
            className="flex items-center gap-2 mb-8 text-[#7A6F60] hover:text-[#1E1E1C] font-bold transition-colors"
          >
            <ArrowLeft size={24} /> Back to Fleet
          </button>

          <div className="bg-white rounded-[3rem] shadow-2xl overflow-hidden">
            {/* IMAGE SLIDER */}
            <div className="relative h-[70vh] md:h-[80vh]">
              <Swiper
 		 modules={[Navigation]}
  		 navigation
  		loop={images.length > 1}
 		 grabCursor
		  allowTouchMove={images.length > 1}
  		className="w-full h-full"
	      >   
		  {images.map((img, i) => (
  <SwiperSlide key={i}>
    <div className="relative w-full h-full flex flex-col items-center justify-center">
      <img  
        src={img}  
        alt={`${selectedScooter.name} ${i + 1}`}  
        onClick={() => setFullscreenImage(img)}  
        className="w-full h-full object-contain bg-black cursor-zoom-in"
      />

      {selectedScooter.name === "ESGOLF" && i === 1 && (
        <div className="absolute bottom-6 bg-white/90 text-black px-6 py-3 rounded-xl text-sm font-bold shadow-lg text-center max-w-lg">
          This is ESGOLF in Electric Cart Mode — attempting to pull it manually will engage the brakes.
        </div>
      )}
    </div>
  </SwiperSlide>
))}
		</Swiper>

              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none"></div>
              <div className="absolute bottom-10 left-10 text-white pointer-events-none">
                <h2 className="text-5xl md:text-7xl font-serif font-bold mb-4">
                  {selectedScooter.name}
                </h2>
                <p className="text-xl md:text-2xl opacity-90 italic">
                  {selectedScooter.tagline}
                </p>
              </div>
            </div>

            {/* CONTENT */}
            <div className="p-12 grid md:grid-cols-2 gap-12">
              <div
                className="max-h-[820px] overflow-y-auto pr-4"
                style={{ WebkitOverflowScrolling: "touch" }}
              >
                <h3
                  className="text-2xl font-bold uppercase tracking-widest mb-6"
                  style={{ color: COLORS.accent }}
                >
                  The Experience
                </h3>

                <p className="text-xl leading-relaxed text-[#1E1E1C] opacity-80 mb-8">
                  {selectedScooter.longDescription}
                </p>

                <div className="bg-[#F9F7F2] p-8 rounded-3xl border border-[#1E1E1C]" style={{ color: COLORS.textPrimary }}>
                  <h4 className="font-bold mb-4">Performance Specs</h4>
                  <ul className="space-y-3">
                    {selectedScooter.specs.map((spec, i) => (
                      <li
                        key={i}
                        className="flex items-center justify-between border-b border-[#E0D8C8] pb-2 last:border-0"
                      >
                        <span>{spec}</span>
                        <CheckCircle size={18} className="text-[#C7A36A]" />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div>
                <h3
                  className="text-2xl font-bold uppercase tracking-widest mb-6"
                  style={{ color: COLORS.accent }}
                >
                  Key Features
                </h3>

                <div className="space-y-6">
                  {selectedScooter.pointers.map((pointer, i) => (
                    <div key={i} className="flex gap-4 items-start">
                      <div className="w-10 h-10 rounded-full bg-[#2F4B3C] flex items-center justify-center flex-shrink-0">
  			<Leaf size={20} color="#C7A36A" />
			</div>
                      <div>
                        <h5 className="text-lg font-bold text-[#1E1E1C]">
 			  {pointer}
			</h5>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-12 pt-8 border-t border-[#E0D8C8]">

  <h3 className="text-xl font-bold mb-2">Pricing & Availability</h3>

  <p className="text-2xl font-semibold text-[#1E1E1C] mb-2">
    ${selectedScooter.price.toLocaleString()}
  </p>

  <p className="text-[#1E3A30] font-bold mb-1 flex items-center gap-2">
    <CheckCircle size={18} /> Immediate Delivery Available
  </p>
  <p className="text-[#555] mb-1">✔ Full Warranty & Service Carried Out Locally </p>
  <p className="text-[#555] mb-6"> {selectedScooter.stock}</p>
  <p className="text-sm text-[#555] mb-4">
	Secure checkout powered by Shopify. Payment available after order confirmation.
  </p>

  <Button
    className="w-full py-5 text-xl"
    onClick={() => startCheckout(selectedScooter)}
  >
    Buy Now
  </Button>
</div>

              </div>
            </div>
          </div>
        </div>

	<AnimatePresence>
  {fullscreenImage && (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-6"
      onClick={() => setFullscreenImage(null)}
    >
      <button
        className="absolute top-6 right-6 text-white hover:rotate-90 transition-transform"
        onClick={() => setFullscreenImage(null)}
      >
        <X size={48} />
      </button>

      <motion.img
        src={fullscreenImage}
        alt="Fullscreen view"
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.3 }}
        className="max-w-[95vw] max-h-[90vh] rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </motion.div>
  )}
</AnimatePresence>
      </motion.section>
     </>
    );
  }

  return (
    <section className="py-20 min-h-screen" style={{ backgroundColor: COLORS.lightSurface }}>
      <SectionHeading
        title="The Lectric Fleet"
        subtitle="Engineering excellence meets fairway freedom."
      />

      <div className="container mx-auto px-6 grid md:grid-cols-3 gap-10">
        {SCOOTERS.map((scooter, idx) => (
          <motion.div
            key={scooter.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: idx * 0.1 }}
            className="bg-white rounded-[2rem] overflow-hidden shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 group border border-[#E0D8C8] cursor-pointer"
            onClick={() => {
              setSelectedScooter(scooter);
              setSlideIndex(0);
            }}
          >
            <div className="relative h-120 overflow-hidden">
              <img
                src={scooter.images?.[0] || scooter.image}
                alt={scooter.name}
		loading="lazy"  decoding="async"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <span className="bg-white/90 text-[#1F332A] px-6 py-3 rounded-full font-bold opacity-0 group-hover:opacity-100 transform scale-90 group-hover:scale-100 transition-all">
                  View Details
                </span>
              </div>
            </div>

            <div className="p-8 text-center">
              <h3 className="text-2xl font-black mb-2 font-serif" style={{ color: COLORS.secondaryBg }}>
                {scooter.name}
              </h3>
              <p className="font-bold italic text-sm tracking-wide mb-4" style={{ color: COLORS.accent }}>
                {scooter.tagline}
              </p>
              <p className="mb-6 leading-relaxed text-base opacity-80 line-clamp-3" style={{ color: COLORS.textPrimary }}>
                {scooter.description}
              </p>
            </div>
          </motion.div>
         
        ))}
      </div>
    </section>
  );
};

export default ScootersPage;
